import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import supabase from '../../supabase';
import './AddExpensePopup.css'

const AddExpensePopup = ({ 
  form, 
  setForm, 
  onSave, 
  onCancel, 
  isEditing = false,
  title = 'Add New Expense',
  saveButtonText = 'Add Expense',
  uploading = false,
  onRefresh // Add this prop to refresh expenses after bulk upload
}) => {
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);

  // Prevent background scrolling when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    setCsvUploading(true);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const user = await supabase.auth.getUser();
          const formatted = results.data.map((row) => ({
            user_id: user.data.user.id,
            category: row.category,
            amount: parseFloat(row.amount),
            description: row.description,
            expense_date: row.expense_date || new Date().toISOString().slice(0, 10),
          }));

          const { error } = await supabase.from("expenses").insert(formatted);
          if (error) {
            console.error(error);
            alert('Upload failed: ' + error.message);
          } else {
            alert(`${formatted.length} expenses uploaded successfully!`);
            setCsvFile(null);
            setShowCSVUpload(false);
            if (onRefresh) onRefresh(); // Refresh the expenses list
            onCancel(); // Close the main popup
          }
        } catch (err) {
          console.error('Unexpected error during upload:', err);
          alert('An unexpected error occurred during upload');
        } finally {
          setCsvUploading(false);
        }
      },
      error: (error) => {
        console.error('Parsing error:', error);
        alert(`Parsing error: ${error.message}`);
        setCsvUploading(false);
      }
    });
  };

  const downloadSampleCSV = () => {
    const csv = Papa.unparse([
      { category: "Travel", amount: "100.00", description: "Taxi fare", expense_date: "2025-05-25" },
      { category: "Food", amount: "50.00", description: "Lunch", expense_date: "2025-05-24" },
      { category: "Office", amount: "200.00", description: "Supplies", expense_date: "2025-05-23" },
    ]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_expenses.csv");
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUploadClick = () => {
    setShowCSVUpload(true);
  };

  return (
    <div className="ExpensePopup__popup-overlay">
      <div className="ExpensePopup__popup">
        <div className="ExpensePopup__popup-header">
          <h3 className="ExpensePopup__popup-title">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="ExpensePopup__popup-close"
          >
            ×
          </button>
        </div>
        
        <div className="ExpensePopup__popup-content">
          <div className="ExpensePopup__field-group">
            <label className="ExpensePopup__label">Category *</label>
            <input 
              name="category"
              placeholder="Enter expense category" 
              className="ExpensePopup__input" 
              value={form.category}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="ExpensePopup__field-group">
            <label className="ExpensePopup__label">Amount *</label>
            <input 
              name="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount" 
              className="ExpensePopup__input" 
              value={form.amount}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="ExpensePopup__field-group">
            <label className="ExpensePopup__label">Description</label>
            <textarea 
              name="description"
              placeholder="Enter description (optional)" 
              className="ExpensePopup__textarea" 
              value={form.description}
              onChange={handleChange}
              rows="3"
            />
          </div>
          
          <div className="ExpensePopup__field-group">
            <label className="ExpensePopup__label">Expense Date</label>
            <input 
              name="expense_date"
              type="date"
              className="ExpensePopup__input" 
              value={form.expense_date}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="ExpensePopup__popup-footer">
          <button
            type="button"
            onClick={onCancel}
            className="ExpensePopup__button ExpensePopup__button--cancel"
            disabled={uploading || csvUploading}
          >
            Cancel
          </button>
          
          {!isEditing && (
            <button
              type="button"
              onClick={handleBulkUploadClick}
              className="ExpensePopup__button ExpensePopup__button--bulk"
              disabled={uploading || csvUploading}
            >
              Bulk Upload
            </button>
          )}
          
          <button
            type="button"
            onClick={onSave}
            className="ExpensePopup__button ExpensePopup__button--save"
            disabled={!form.category.trim() || !form.amount || uploading || csvUploading}
          >
            {uploading ? 'Processing...' : saveButtonText}
          </button>
        </div>
      </div>

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="ExpensePopup__csv-overlay">
          <div className="ExpensePopup__csv-popup">
            <div className="ExpensePopup__popup-header">
              <h3 className="ExpensePopup__popup-title">Bulk Upload Expenses</h3>
              <button
                type="button"
                onClick={() => setShowCSVUpload(false)}
                className="ExpensePopup__popup-close"
              >
                ×
              </button>
            </div>
            
            <div className="ExpensePopup__popup-content">
              <div className="ExpensePopup__field-group">
                <label className="ExpensePopup__label">Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="ExpensePopup__input"
                />
              </div>
              
              <div className="ExpensePopup__csv-actions">
                <button
                  className="ExpensePopup__button ExpensePopup__button--secondary"
                  onClick={downloadSampleCSV}
                  disabled={csvUploading}
                >
                  Download Sample CSV
                </button>
              </div>
            </div>
            
            <div className="ExpensePopup__popup-footer">
              <button
                type="button"
                onClick={() => setShowCSVUpload(false)}
                className="ExpensePopup__button ExpensePopup__button--cancel"
                disabled={csvUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCSVUpload}
                className="ExpensePopup__button ExpensePopup__button--save"
                disabled={!csvFile || csvUploading}
              >
                {csvUploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddExpensePopup;
